import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiArrowUpRight, FiBookOpen, FiClock } from 'react-icons/fi';
import { SERVER_BASE_URL, getImageUrl } from '../utils/api';
import { getPersonaLabel } from '../utils/expertRef';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { expertTheme, focusRing, reducedMotion } from '../theme/expertTheme';

const Page = styled.div`
  min-height: 100vh;
  padding: 28px 0 112px;
  background: radial-gradient(circle at 92% 4%, rgba(105,125,84,.12), transparent 28rem), ${expertTheme.colors.canvas};
  color: ${expertTheme.colors.ink};
`;

const Container = styled.div`
  width: min(${expertTheme.container}, calc(100% - 48px));
  margin: 0 auto;
  @media (max-width: 640px) { width: min(100% - 32px, ${expertTheme.container}); }
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 22px;
  color: #85877f;
  font-size: 13px;
  a { display: inline-flex; align-items: center; gap: 7px; color: ${expertTheme.colors.sage}; font-weight: 750; ${focusRing} }
`;

const Article = styled.article`
  overflow: hidden;
  border-radius: 34px;
  background: ${expertTheme.colors.surface};
  box-shadow: ${expertTheme.shadow.soft};
`;

const HeroImage = styled.div`
  min-height: clamp(320px, 54vw, 650px);
  background: #dcd7cc center / cover no-repeat;
`;

const Header = styled.header`
  max-width: 940px;
  margin: -86px auto 0;
  position: relative;
  padding: clamp(28px, 5vw, 62px);
  border-radius: ${expertTheme.radius.large} ${expertTheme.radius.large} 0 0;
  background: ${expertTheme.colors.surface};

  @media (max-width: 720px) {
    margin: -34px 16px 0;
    padding: 28px 20px;
    border-radius: 24px 24px 0 0;
  }
`;

const Category = styled.span`
  display: inline-flex;
  padding: 7px 12px;
  border-radius: ${expertTheme.radius.pill};
  background: ${expertTheme.colors.sageSoft};
  color: ${expertTheme.colors.sage};
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .1em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  max-width: 850px;
  margin: 20px 0 24px;
  color: ${expertTheme.colors.ink};
  font-family: var(--font-body);
  font-size: clamp(2.35rem, 5vw, 5rem);
  font-weight: 760;
  line-height: .99;
  letter-spacing: -.052em;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 24px;
  color: ${expertTheme.colors.inkSoft};
  font-size: 14px;
`;

const Author = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 11px;
  color: ${expertTheme.colors.ink};
  font-weight: 750;
  ${focusRing}
  img, span.avatar {
    width: 42px;
    height: 42px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    object-fit: cover;
    object-position: top;
    background: ${expertTheme.colors.accent};
    color: #fff;
  }
  small { display: block; color: ${expertTheme.colors.inkSoft}; font-weight: 500; }
`;

const ReadTime = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
`;

const Body = styled.div`
  max-width: 790px;
  margin: 0 auto;
  padding: 12px 24px clamp(50px, 8vw, 92px);
`;

const Content = styled.div`
  color: #3f423c;
  font-size: clamp(16px, 1.7vw, 19px);
  line-height: 1.8;
  p { margin: 0 0 24px; }
  h2, h3 {
    margin: 46px 0 18px;
    color: ${expertTheme.colors.ink};
    font-family: var(--font-body);
    line-height: 1.15;
    letter-spacing: -.025em;
  }
  h2 { font-size: clamp(1.9rem, 3vw, 2.8rem); }
  h3 { font-size: clamp(1.5rem, 2.4vw, 2.1rem); }
  ul, ol { margin: 24px 0; padding-left: 24px; }
  ul { list-style: disc; }
  ol { list-style: decimal; }
  li { margin-bottom: 10px; }
  blockquote {
    margin: 38px 0;
    padding: 28px 30px;
    border: 0;
    border-radius: ${expertTheme.radius.medium};
    background: ${expertTheme.colors.sageSoft};
    color: ${expertTheme.colors.ink};
    font-size: 1.1em;
    font-style: normal;
  }
  img { border-radius: ${expertTheme.radius.medium}; }
  a { color: ${expertTheme.colors.accentDark}; text-decoration: underline; text-underline-offset: 3px; }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 44px;
  padding-top: 28px;
  border-top: 1px solid ${expertTheme.colors.line};
`;

const Tag = styled.span`
  padding: 7px 12px;
  border-radius: ${expertTheme.radius.pill};
  background: ${expertTheme.colors.canvas};
  color: ${expertTheme.colors.inkSoft};
  font-size: 13px;
`;

const Related = styled.section`
  padding-top: clamp(64px, 8vw, 100px);
  h2 { margin: 0 0 28px; font-family: var(--font-body); font-size: clamp(2rem, 4vw, 3.6rem); color: ${expertTheme.colors.ink}; letter-spacing: -.04em; }
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0,1fr)); }
  @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

const RelatedCard = styled(Link)`
  overflow: hidden;
  border-radius: ${expertTheme.radius.medium};
  background: #fff;
  box-shadow: 0 10px 32px rgba(42,38,30,.07);
  transition: transform 200ms ease, box-shadow 200ms ease;
  ${focusRing}
  ${reducedMotion}
  &:hover { transform: translateY(-5px); box-shadow: ${expertTheme.shadow.hover}; }
`;

const RelatedImage = styled.div`
  aspect-ratio: 16 / 10;
  background: #dcd7cc center / cover no-repeat;
`;

const RelatedBody = styled.div`
  padding: 22px;
  h3 { margin: 0 0 20px; color: ${expertTheme.colors.ink}; font-family: var(--font-body); font-size: 20px; line-height: 1.25; letter-spacing: -.02em; }
  span { display: flex; align-items: center; justify-content: space-between; color: ${expertTheme.colors.sage}; font-size: 13px; font-weight: 800; }
`;

const State = styled.div`
  min-height: 460px;
  display: grid;
  place-items: center;
  text-align: center;
  h1 { font-family: var(--font-body); }
  p { color: ${expertTheme.colors.inkSoft}; }
  a { color: ${expertTheme.colors.accent}; font-weight: 800; }
`;

function parseTags(tags) {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  if (typeof tags !== 'string') return [];
  if (tags.startsWith('[')) {
    try { return JSON.parse(tags); } catch { return []; }
  }
  return tags.split(tags.includes(',') ? ',' : ' ').map(tag => tag.trim()).filter(Boolean);
}

function getNewsImageUrl(imagePath) {
  if (!imagePath) return '/news_pic/1.png';
  if (imagePath.startsWith('data:image/')) return imagePath;
  return getImageUrl(imagePath) || '/news_pic/1.png';
}

const NewsDetail = () => {
  const { slug } = useParams();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetch(`${SERVER_BASE_URL}/api/news/${slug}`, { signal: controller.signal })
      .then(response => response.json())
      .then(result => {
        if (!result.success || !result.data) throw new Error('not-found');
        setNews(result.data);
        setRelatedNews(result.relatedNews || []);
        setStatus('success');
      })
      .catch(error => { if (error.name !== 'AbortError') setStatus('error'); });
    return () => controller.abort();
  }, [slug]);

  const tags = useMemo(() => parseTags(news?.tags), [news?.tags]);

  if (status === 'loading') return <Page><State><p>Загружаем материал…</p></State></Page>;
  if (status === 'error' || !news) return <Page><State><div><h1>Материал не найден</h1><p>Возможно, публикация была перемещена.</p><Link to="/news">Вернуться в журнал</Link></div></State></Page>;

  const imageUrl = getNewsImageUrl(news.main_image);
  const authorName = news.expert_display_name || news.author;
  const initials = (authorName || 'L').split(' ').map(part => part[0]).join('').slice(0, 2);

  return (
    <Page>
      <Container>
        <Breadcrumb><Link to="/news"><FiArrowLeft /> Журнал</Link><span>/</span><span>Материал</span></Breadcrumb>
        <Article>
          <HeroImage role="img" aria-label={news.title} style={{ backgroundImage: `url(${imageUrl})` }} />
          <Header>
            {(news.category_name || news.category) ? <Category>{news.category_name || news.category}</Category> : null}
            <Title>{news.title}</Title>
            <Meta>
              {news.expert_slug ? <Author to={`/expert/${news.expert_slug}`}>{news.expert_photo_url ? <img src={getImageUrl(news.expert_photo_url)} alt="" /> : <span className="avatar">{initials}</span>}<span>{authorName}<small>{getPersonaLabel(news.expert_persona_type)}</small></span></Author> : authorName ? <span>{authorName}</span> : null}
              {news.read_time ? <ReadTime><FiClock /> {news.read_time} мин. чтения</ReadTime> : null}
            </Meta>
          </Header>
          <Body>
            <Content dangerouslySetInnerHTML={{ __html: sanitizeHtml(news.content) }} />
            {tags.length ? <Tags>{tags.map(tag => <Tag key={tag}>{tag}</Tag>)}</Tags> : null}
          </Body>
        </Article>

        {relatedNews.length ? <Related><h2>Продолжить чтение</h2><RelatedGrid>{relatedNews.map(item => <RelatedCard key={item.id} to={item.slug ? `/news/${item.slug}` : `/news/${item.id}`}><RelatedImage style={{ backgroundImage: `url(${getNewsImageUrl(item.main_image)})` }} /><RelatedBody><h3>{item.title}</h3><span><span><FiBookOpen /> Читать</span><FiArrowUpRight /></span></RelatedBody></RelatedCard>)}</RelatedGrid></Related> : null}
      </Container>
    </Page>
  );
};

export default NewsDetail;
